import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import AboutBannerImage2 from "../../src/assets/images/aboutbanner2.jpeg";
import { getFAQS } from '../api/api';

const FAQSection = () => {
    const [openFaq, setOpenFaq] = useState(null);
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchFAQs = async () => {
            try {
                setLoading(true);
                // Pass "course" as type parameter to filter at API level
                const response = await getFAQS("course");
                console.log("FAQ API Response:", response);
                
                if (response && response.status === true) {
                    // Since we're filtering at API level, no need to filter again
                    // Just map the data directly
                    const mappedFAQs = response.data.map(item => ({
                        id: item.id,
                        question: item.title,
                        answer: item.description?.replace(/<[^>]*>/g, ''), // Strip HTML tags for plain text
                        answerHtml: item.description // Keep original HTML for rich text rendering
                    }));
                    
                    setFaqs(mappedFAQs);
                    console.log("Mapped FAQs:", mappedFAQs);
                    setError(null);
                } else {
                    console.error("Failed to load FAQs:", response?.message);
                    setFaqs([]);
                    setError(response?.message || "No FAQs found");
                }
            } catch (error) {
                console.error("Error fetching FAQs:", error);
                setFaqs([]);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchFAQs();
    }, []);

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? null : id);
    };

    if (loading) {
        return (
            <div className="w-full bg-white py-16 px-6">
                <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="w-full bg-white py-16 px-6">
                <div className="custom-padding xl-screen-padding">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <p className="text-green-500 text-sm tracking-widest font-semibold mb-2">
                            GOT QUESTIONS?
                        </p>
                        <h2 className="text-3xl md:text-4xl font-semibold font-merriweather text-gray-800 mb-4">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-gray-600 text-md font-lato">
                            Find answers to common questions about our platform and courses.
                        </p>
                    </div>

                    {/* FAQ Items */}
                    {!loading && faqs.length > 0 ? (
                        <div className="space-y-4">
                            {faqs.map((faq) => (
                                <div 
                                    key={faq.id}
                                    className="border border-gray-200 rounded-lg overflow-hidden"
                                >
                                    {/* Question Button */}
                                    <button
                                        onClick={() => toggleFaq(faq.id)}
                                        className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors duration-200 flex justify-between items-center"
                                    >
                                        <span className="font-semibold text-gray-800 text-lg font-lato">
                                            {faq.question}
                                        </span>
                                        {openFaq === faq.id ? (
                                            <FiChevronUp className="text-gray-600 text-xl flex-shrink-0" />
                                        ) : (
                                            <FiChevronDown className="text-gray-600 text-xl flex-shrink-0" />
                                        )}
                                    </button>

                                    {/* Answer (conditionally rendered) */}
                                    {openFaq === faq.id && (
                                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 font-lato">
                                            {/* Use dangerouslySetInnerHTML to render HTML content */}
                                            <div 
                                                className="text-gray-600 leading-relaxed"
                                                dangerouslySetInnerHTML={{ __html: faq.answerHtml }}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        !loading && !error && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">No FAQs available at the moment.</p>
                            </div>
                        )
                    )}

                    {/* Error Message */}
                    {error && (
                        <div className="text-center py-8">
                            <p className="text-red-500">Error loading FAQs: {error}</p>
                        </div>
                    )}

                    {/* Optional: Contact Support */}
                    <div className="text-center mt-8">
                        {/* Add any additional content here */}
                    </div>
                </div>
            </div>
            
            <div
                className="relative w-full h-[400px] flex items-center justify-center text-center px-6"
                style={{
                    backgroundImage: `url(${AboutBannerImage2})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/50"></div>

                {/* Content */}
                <div className="relative z-10 max-w-3xl">
                    <h2 className="text-white text-3xl md:text-5xl leading-snug font-merriweather">
                        Start Your Nutrition Learning Journey Today
                    </h2>

                    <p className="text-gray-200 mt-4 text-sm md:text-base">
                        Gain professional nutrition knowledge, earn verified certificates, and
                        unlock new career enhancement opportunities with Nutrillion Academy.
                    </p>

                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                        {/* Primary Button */}
                        <button className="bg-[#68b046] text-white px-6 py-3 rounded-lg text-md font-medium transition">
                            Browse Courses
                        </button>

                        {/* Secondary Button */}
                        <button className="border-2 border-white text-white px-6 py-3 rounded-lg text-md font-medium">
                            Join Nutrillion Academy
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FAQSection;